// ============================================================
// HARMONIC CHARTER — Auth Store (Zustand)
// ============================================================

import { create } from 'zustand';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface AuthStore {
  user:    User | null;
  session: Session | null;
  loading: boolean;
  error:   string | null;

  // Actions
  signUp:   (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  signIn:   (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  signOut:  () => Promise<void>;
  init:     () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user:    null,
  session: null,
  loading: true,
  error:   null,

  init: async () => {
    // Get initial session
    const { data: { session } } = await supabase.auth.getSession();
    set({ session, user: session?.user ?? null, loading: false });

    // Listen for auth changes — also clears loading so App.tsx
    // only fires cloud sync once the session is fully confirmed.
    supabase.auth.onAuthStateChange((_event, session) => {
      set({ session, user: session?.user ?? null, loading: false });
    });
  },

  signUp: async (email, password) => {
    set({ loading: true, error: null });
    const { error } = await supabase.auth.signUp({ email, password });
    set({ loading: false });
    if (error) {
      set({ error: error.message });
      return { ok: false, error: error.message };
    }
    return { ok: true };
  },

  signIn: async (email, password) => {
    set({ loading: true, error: null });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    set({ loading: false });
    if (error) {
      set({ error: error.message });
      return { ok: false, error: error.message };
    }
    set({ session: data.session, user: data.user });
    return { ok: true };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    localStorage.clear();
    set({ user: null, session: null });
  },

  clearError: () => set({ error: null }),
}));